import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import axios from 'axios';
import 'react-datepicker/dist/react-datepicker.css';
import 'react-datepicker/dist/react-datepicker.css';
import './ExpenseViewPage.css';
import { useTranslation } from 'react-i18next';
import DatePicker from 'react-datepicker';

const API_BASE_URL = process.env.REACT_APP_EZTS_API_BASE_URL;

const statusColors = {
  draft: '#bdbdbd',
  pending: '#1976d2',
  approved: '#388e3c',
  rejected: '#d32f2f',
};

const expenseTypes = {
  1: 'Travel',
  2: 'Meals',
  3: 'Lodging',
  4: 'Office Supplies',
  5: 'Software',
  6: 'Equipment',
  7: 'Other'
};

const ExpenseViewPage = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { id: paramId } = useParams();
  const { user } = useSelector((state) => state.auth);
  const navigate = useNavigate();
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [receipts, setReceipts] = useState([]);

  // Get expense sheet data from navigation state if available
  const navState = location.state && location.state.expenseSheet;
  const id = paramId;

  useEffect(() => {
    let ignore = false;
    async function fetchExpenseSheet() {
      setLoading(true);
      setError(null);
      try {
        if (navState && navState.id === id) {
          const exp = navState;
          setForm({
            project: exp.project?.projectCode || '-',
            expenseType: exp.expenseTypeId || '-',
            amount: exp.expenseAmount || null,
            date: exp.expenseDate || null,
            description: exp.description || '-',
            status: exp.status || '-',
            orgName: exp.department?.costCenter?.organization?.orgName || '-',
            costCenter: exp.department?.costCenter?.ccName || '-',
            deptName: exp.department?.deptName || '-',
            user: `${exp.user?.firstName || ""} ${exp.user?.lastName || ""}`.trim() || "-",
            approver: exp.approver || '-',
          });
          setLoading(false);
          return;
        }
        if (!user || !id) throw new Error('Missing auth or id');
        const response = await axios.get(
          `${API_BASE_URL}expense/all?orgId=${user.department?.costCenter?.organization?.orgId}&approverId=${user.userId}`
        );
        const apiData = response.data || [];
        const found = apiData.find(exp => String(exp.expenseId) === String(id));
        if (!found) throw new Error('Expense Sheet not found');

        // Map status number to string
        const statusMap = { 1: 'draft', 2: 'pending', 3: 'approved', 4: 'rejected' };

        setForm({
          project: found.project?.projectCode || '-',
          expenseType: found.expenseTypeId || '-',
          amount: found.expenseAmount || null,
          date: new Date(found.expenseDate) || null,
          description: found.description || '-',
          status: statusMap[found.status] || '-',
          orgName: found.department?.costCenter?.organization?.orgName || '-',
          costCenter: found.department?.costCenter?.ccName || '-',
          deptName: found.department?.deptName || '-',
          user: `${found.user?.firstName || ""} ${found.user?.lastName || ""}`.trim() || "-",
          approver: found.approver ? `${found.approver.firstName || ''} ${found.approver.lastName || ''}`.trim() : '',
        });
        setReceipts(found.receipts || []);
      } catch (err) {
        setError(err.message || 'Error loading Expense Sheet');
      } finally {
        setLoading(false);
      }
    }
    fetchExpenseSheet();
    return () => { ignore = true; };
  }, [id, user, navState]);

  if (loading) {
    return <div className="exp-edit-container">{t('loading', 'Loading...')}</div>;
  }
  if (error) {
    return <div className="exp-edit-container" style={{ color: 'red' }}>{t('error', error)}</div>;
  }

  return (
    <div className="exp-edit-container">
      <h2 className="exp-edit-title">{t('approvalsViewPage.expenseInfo')}</h2>
      <div className="exp-edit-status-row">
        <div className="exp-edit-status" style={{ background: statusColors[form.status] || '#bdbdbd' }}>
          {t('approvalsViewPage.status')}: {t(`approvalsViewPage.${form.status}`, form.status.charAt(0).toUpperCase() + form.status.slice(1))}
        </div>
      </div>
      <div className="exp-edit-meta-stack">
        {id && (
          <div className="exp-edit-meta-line">
            {t('approvalsViewPage.id')}: {id}
          </div>
        )}
        <div className="exp-edit-meta-line">
          {t('approvalsViewPage.org')}: {form.orgName || '-'}
        </div>
        <div className="exp-edit-meta-line">
          {t('approvalsViewPage.costCenter')}: {form.costCenter || '-'}
        </div>
        <div className="exp-edit-meta-line">
          {t('approvalsViewPage.dept')}: {form.deptName || '-'}
        </div>
        <div className="exp-edit-meta-line">
          {t('approvalsViewPage.user')}: {form.user || '-'}
        </div>
        <div className="exp-edit-meta-line">
          {t('approvalsViewPage.approver')}: {form.approver || '-'}
        </div>
      </div>

      <form onSubmit={exp => exp.preventDefault()}>
        <div className="exp-edit-form-section">
          <div className="exp-edit-form-group">
            <label>{t('approvalsViewPage.project')}:</label>
            <input
              type="text"
              name="project"
              value={form.project}
              disabled={true}
              className="exp-edit-input"
            />
          </div>
          <div className="exp-edit-form-group">
            <label>{t('approvalsViewPage.expenseType')}:</label>
            <input
              type="text"
              name="expenseType"
              value={expenseTypes[form.expenseType]}
              disabled={true}
              className="exp-edit-input"
            />
          </div>
          <div className="exp-edit-form-group">
            <label>{t('approvalsViewPage.amount')} ($):</label>
            <input
              type="text"
              name="amount"
              value={form.amount}
              disabled={true}
              className="exp-edit-input"
              min="0"
              step="0.01"
              placeholder="0.00"
              pattern="^\\d+(\\.\\d{1,2})?$"
              maxLength={12}
            />
          </div>
          <div className="exp-edit-form-group">
            <label>{t('approvalsViewPage.date')}:</label>
            <DatePicker
              selected={form.date}
              disabled={true}
              className="exp-edit-input"
              wrapperClassName="exp-edit-datepicker-wrapper"
              placeholderText={t('approvalsViewPage.selectDate')}
              dateFormat="yyyy-MM-dd"
            />
          </div>
          <div className="exp-edit-form-group">
            <label>{t('approvalsViewPage.comment')}:</label>
            <textarea
              name="description"
              value={form.description}
              disabled={true}
              className="exp-edit-input exp-edit-textarea"
              placeholder={t('approvalsViewPage.addComment')}
              rows="3"
              maxLength={512}
            />
          </div>
        </div>

        <div className="exp-edit-btn-row">
          <button type="button" onClick={() => navigate('/approvals/expenseapprovals')} className="exp-edit-btn cancel">
            {t('approvalsViewPage.cancel')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ExpenseViewPage;
